import {ChangeDetectorRef, Component, inject, OnDestroy, OnInit, signal} from "@angular/core";
import {
    ControlContainer,
    UntypedFormBuilder as FormBuilder,
    UntypedFormControl as FormControl,
    UntypedFormGroup as FormGroup,
    Validators
} from "@angular/forms";
import {Utils} from "../../../../../core/helpers/Utils";
import {debounceTime, firstValueFrom, pairwise, startWith, Subscription} from "rxjs";
import {CisService} from "../../../../../service/services/rest/cis/cis.service";
import {GlobalAccountModel} from "../../../../../core/models/cis/GlobalAccount.model";
import {SubaccountModel} from "../../../../../core/models/cis/Subaccount.model";
import {CustomValidators} from "../../../../../core/helpers/CustomValidators";
import {
    IsBTPApplication,
    IsReusingSPC,
    LandscapesKeyToDisplayName,
    OperationalMode,
    OperationalScenario,
    WizardUtils,
} from "../../shared/WizardHelpers";
import {FrontendConfigService} from "../../../../../service/services/misc/frontend-config/frontend-config.service";
import {Const, ResourceType} from "../../../../../core/helpers/Const";
import {PAYLOAD_FORM_CONTROLS, PAYLOAD_FORM_GROUPS} from "../unified-create-application-dialog.component";
import {DialogRef} from "@fundamental-ngx/core/dialog";
import {NestedValuePipe} from "../../../../../shared/pipes/nested-value/nested-value.pipe";
import {ControlLinker} from "../../shared/ControlLinker";
import {HttpClient} from "@angular/common/http";
import {ServiceModel} from "../../../../../core/models/misc/service.model";
import {AppConfigService} from "../../../../../service/services/misc/app-config/app-config.service";
import {DialogService} from "../../../../../service/services/misc/dialog/dialog.service";
import {
    ConfirmationDialogComponent,
    ConfirmationDialogConfig
} from "../../../../../shared/dialogs/confirmation-dialog/confirmation-dialog.component";

export enum CapabilityType {
    Commercialization = "commercialization",
    Integration = "integration",
    Provisioning = "provisioning",
    Metering = "metering"
}

export const STEP_SETUP_FORM_GROUPS = {
    STEP_SETUP: "step1",
    OPERATIONAL_DATA: "operationalData",
    CAPABILITIES: "capabilities",
    PRODUCT_METADATA: "productMetadata",
    METERING: "metering",
    PROVISIONING: "provisioning"
};
export const STEP_SETUP_FORM_CONTROLS = {
    APP_DISPLAY_NAME: "appDisplayName",
    OPERATIONAL_MODE: "operationalMode",
    IS_BTP_APPLICATION: "isBTPApplication",
    IS_REUSING_SPC: "isReusingSpc",
    SCENARIO: "scenario",
    GLOBAL_ACCOUNT: "globalAccount",
    SUBACCOUNT: "subaccount",
    TEMPLATE_SUBACCOUNT: "templateSubaccount",
    LANDSCAPE: "landscape",
    CLD_SYSTEM_ROLE: "cldSystemRole",
    TENANT_PRODUCT_TYPE: "tenantProductType",
    LOCAL_TENANT_TYPE: "localTenantType",
    SERVICE: "service",
    STAGING_PARAMETER: "stagingParameter"
};
export const APP_DISPLAY_NAME_PATTERN =
    /^[a-zA-Z](?:[a-zA-Z0-9\s]*[a-zA-Z0-9])?$/;
export const CUSTOM_IDP_MSG =
    `You're logged in with a custom OIDC provider. To view and select SAP BTP entities, log in with SAP ID service.`;

export const OPSCURITY_IDP_MSG =
    `You're logged in with the Opscurity IdP. To view and select SAP BTP entities, log in with SAP ID service.`;

@Component({
    selector: "app-unified-step-setup",
    templateUrl: "./unified-step-setup.component.html",
    styleUrls: ["./unified-step-setup.component.scss"],
    standalone: false
})
export class UnifiedStepSetupComponent implements OnInit, OnDestroy {
    public dialogRef: DialogRef = inject(DialogRef);
    private readonly controlContainer: ControlContainer = inject(ControlContainer);
    private readonly fb: FormBuilder = inject(FormBuilder);
    private readonly cisService: CisService = inject(CisService);
    private readonly nestedValuePipe: NestedValuePipe = inject(NestedValuePipe);
    private readonly frontendConfigService: FrontendConfigService = inject(FrontendConfigService);
    private readonly cdRef: ChangeDetectorRef = inject(ChangeDetectorRef);
    private readonly httpClient: HttpClient = inject(HttpClient);
    private readonly _appConfigService: AppConfigService = inject(AppConfigService);
    private readonly _dialogService: DialogService = inject(DialogService);

    readonly Utils = Utils;
    readonly Const = Const;
    readonly OperationalMode = OperationalMode;
    readonly OperationalScenario = OperationalScenario;
    readonly CapabilityType = CapabilityType;
    readonly IsBTPApplication = IsBTPApplication;
    readonly IsReusingSPC = IsReusingSPC;
    readonly STEP_SETUP_FORM_CONTROLS = STEP_SETUP_FORM_CONTROLS;
    readonly STEP_SETUP_FORM_GROUPS = STEP_SETUP_FORM_GROUPS;
    readonly PAYLOAD_FORM_GROUPS = PAYLOAD_FORM_GROUPS;
    readonly PAYLOAD_FORM_CONTROLS = PAYLOAD_FORM_CONTROLS;
    public form: FormGroup;

    globalAccounts: GlobalAccountModel[];
    subaccounts: SubaccountModel[];
    isGlobalAccountsBusy = false;
    isSubaccountsBusy = false;
    isReusingSPC = false;
    isBTPScenarioVisible = false;
    operationalMode: OperationalMode = undefined;
    operationalScenario: OperationalScenario = undefined;
    isOfferedInSapBtp = true;
    scenarioFieldDisabled = false;
    isBTPAccountsVisible = false;
    isServiceVisible = false;
    defaultLandscape: string;
    spcSpfiEndpoint: string;
    stagingParameters: Array<{ displayName: string; infix: string, wizardName: string }> = [];
    showStagingParameters = false;
    subscriptions: Subscription[] = [];
    landscapeValues: { title: string; key: string; }[] = [];
    cldSystemRoleLinker = new ControlLinker<FormControl>();
    tenantProductTypeLinker = new ControlLinker<FormControl>();
    localTenantTypeLinker = new ControlLinker<FormControl>();
    services = signal<ServiceModel[]>([]);
    isServicesBusy = false;
    servicesJsonUrl = 'assets/services/services.json';
    oidc: string = '';
    oidcMessage = signal('');

    constructor() {
    }


    async ngOnInit() {
        this.form = <FormGroup>this.controlContainer.control;
        this.landscapeValues = await this.initFrontendConfigs();
        this.initControls();
        this.initSubscriptions();
        if (!this.services() || this.services().length === 0) {
            void this.loadServices();
        }
        const userInfo = this._appConfigService.getUserInfoModel();
        this.oidc = userInfo?.oidc || "sap.ids";
        this.updateOidcMessage();
    }

    updateOidcMessage(): void {
        if (this.oidc === 'custom.idp') {
            this.oidcMessage.set(CUSTOM_IDP_MSG);
        } else if (this.oidc === 'sap.opscurity') {
            this.oidcMessage.set(OPSCURITY_IDP_MSG);
        } else {
            this.oidcMessage.set('');
        }
    }

    async initFrontendConfigs() {
        const frontendConfig = this.frontendConfigService.getFrontendConfigModel();
        const allLandscapes = Object.entries(LandscapesKeyToDisplayName).map(([key, displayName]) => ({
            title: displayName,
            key: key
        })).filter(item => frontendConfig.landscapeWizardAvailability?.[item.key]);

        this.defaultLandscape = allLandscapes[0]?.key;
        this.spcSpfiEndpoint = frontendConfig.spfiEndpoint;
        this.stagingParameters = frontendConfig.stagingParameters || [];
        this.showStagingParameters = this.stagingParameters.length && Utils.hasFeatureFlag(Const.FEATURE_STAGING_PARAMETERS);

        return allLandscapes;
    }

    initSubscriptions() {
        this.subscribeToOperationalModeChanges();
        this.subscribeToOperationalDataChange();
        this.subscribeToAppDisplayNameChanges();
        this.subscribeToStagingParameterChanges();
        this.subscribeToProvisioningChanges();
    }

    subscribeToAppDisplayNameChanges() {
        const appDisplayNameControl = this.form.get(STEP_SETUP_FORM_GROUPS.STEP_SETUP)?.get(STEP_SETUP_FORM_CONTROLS.APP_DISPLAY_NAME);
        if (!appDisplayNameControl) {
            return;
        }
        const subscription = appDisplayNameControl.valueChanges.subscribe(this.onAppDisplayNameChange.bind(this));
        this.subscriptions.push(subscription);
    }

    subscribeToStagingParameterChanges() {
        const stagingParameterControl = this.form.get(STEP_SETUP_FORM_GROUPS.STEP_SETUP)?.get(STEP_SETUP_FORM_CONTROLS.STAGING_PARAMETER);
        if (!stagingParameterControl) {
            return;
        }
        const subscription = stagingParameterControl.valueChanges.subscribe(() => {
            const appDisplayNameControl = this.form.get(STEP_SETUP_FORM_GROUPS.STEP_SETUP)?.get(STEP_SETUP_FORM_CONTROLS.APP_DISPLAY_NAME);
            if (appDisplayNameControl) {
                this.onAppDisplayNameChange(appDisplayNameControl.value);
            }
            this.adjustMeteringControls(!this.hasStagingInfix());
        });
        this.subscriptions.push(subscription);
    }

    subscribeToOperationalModeChanges() {
        const formGroup = this.getOperationalDataFormGroup();
        const subscription = formGroup.get(STEP_SETUP_FORM_CONTROLS.OPERATIONAL_MODE).valueChanges.subscribe(this.onOperationalModeChange.bind(this));
        this.subscriptions.push(subscription);
    }

    subscribeToProvisioningChanges() {
        const formGroup = this.getProvisioningFormGroup();
        this.subscriptions.push(formGroup.get(STEP_SETUP_FORM_CONTROLS.SCENARIO).valueChanges.subscribe(this.onBTPScenarioChange.bind(this)));
        this.subscriptions.push(formGroup.get(STEP_SETUP_FORM_CONTROLS.IS_BTP_APPLICATION).valueChanges.subscribe(this.onProvisioningChanges.bind(this)));
        this.subscriptions.push(formGroup.get(STEP_SETUP_FORM_CONTROLS.IS_REUSING_SPC).valueChanges.subscribe(this.onProvisioningChanges.bind(this)));
        this.subscriptions.push(formGroup.get(STEP_SETUP_FORM_CONTROLS.LANDSCAPE).valueChanges.subscribe(this.onLandscapeChange.bind(this)));
        formGroup.get(STEP_SETUP_FORM_CONTROLS.GLOBAL_ACCOUNT).valueChanges.pipe(
            debounceTime(100)).subscribe(this.onGlobalAccountChange.bind(this));
    }

    subscribeToOperationalDataChange() {
        const formGroup = this.getOperationalDataFormGroup();
        const subscription = formGroup.valueChanges.pipe(startWith(null as string), pairwise())
            .subscribe(([prev, next]: [any, any]) => this.onOperationalDataChange(prev, next));
        this.subscriptions.push(subscription);
    }

    async loadGlobalAccounts(landscape: string) {
        this.isGlobalAccountsBusy = true;
        try {
            this.globalAccounts = await firstValueFrom(this.cisService.getGlobalAccounts(landscape));
        } catch (e) {
            this.globalAccounts = [];
        } finally {
            this.isGlobalAccountsBusy = false;
        }
    }

    async loadSubaccounts(gaSubdomain: string, landscape: string) {
        this.isSubaccountsBusy = true;
        try {
            this.subaccounts = await firstValueFrom(this.cisService.getSubaccounts(gaSubdomain, landscape));
        } catch (e) {
            this.subaccounts = [];
        } finally {
            this.isSubaccountsBusy = false;
        }
    }

    async loadServices() {
        this.isServicesBusy = true;
        try {
            this.services.set(await firstValueFrom(this.getServices()));
        } catch (e) {
            this.services = signal<ServiceModel[]>([]);
        } finally {
            this.isServicesBusy = false;
        }
    }

    showBtpApplication() {
        return (this.isCustomerManaged() || this.isOfferedInSapBtp);
    }

    isCustomerManaged() {
        return this.operationalMode === OperationalMode.CUSTOMER;
    }

    isSapManaged() {
        return this.operationalMode === OperationalMode.SAP;
    }

    onOperationalModeChange(operationalMode: OperationalMode) {
        this.operationalMode = operationalMode;
        const isProvisioningEnabled = this.getOperationalDataFormGroup().get(STEP_SETUP_FORM_GROUPS.CAPABILITIES).value[CapabilityType.Provisioning];
        this.setLandscapeValue();
        if (this.showBtpApplication() && isProvisioningEnabled) {
            this.setScenarioField();
        }
    }

    setLandscapeValue() {
        const landscapeFormControl = this.getProvisioningFormGroup().get("landscape");
        const isProvisioningEnabled = this.getOperationalDataFormGroup().get(STEP_SETUP_FORM_GROUPS.CAPABILITIES).value[CapabilityType.Provisioning];
        landscapeFormControl.setValue((isProvisioningEnabled && (this.operationalMode === OperationalMode.CUSTOMER || this.isOfferedInSapBtp || this.operationalScenario === OperationalScenario.UCA_LEASED)) ? this.defaultLandscape : null);
    }

    setScenarioField() {
        const formGroup = this.getProvisioningFormGroup();
        const scenarioField = formGroup.get(STEP_SETUP_FORM_CONTROLS.SCENARIO);

        this.isReusingSPC = formGroup.get(STEP_SETUP_FORM_CONTROLS.IS_REUSING_SPC).value;
        this.isOfferedInSapBtp = formGroup.get(STEP_SETUP_FORM_CONTROLS.IS_BTP_APPLICATION).value === "yes";
        scenarioField.enable();
        if(this.isCustomerManaged()) {
            scenarioField?.setValue(OperationalScenario.UCA_LEASED);
            scenarioField.disable();
        } else {
            if(!this.isOfferedInSapBtp && this.isReusingSPC) {
                scenarioField?.setValue(OperationalScenario.SPC_SPFI);
                scenarioField.disable();
            } else {
                scenarioField?.setValue(OperationalScenario.UCA_LEASED);
            }

        }
    }

    onProvisioningChanges() {
        this.setScenarioField();
        this.adjustProvisioningControls(true);
    }

    onOperationalDataChange(prev: any, next: any) {

        const isOperationalModeChanged = prev?.operationalMode !== next.operationalMode;

        const isProvisioningChanged = prev?.capabilities?.[CapabilityType.Provisioning] !== next.capabilities[CapabilityType.Provisioning];
        const isIntegrationChanged = prev?.capabilities?.[CapabilityType.Integration] !== next.capabilities[CapabilityType.Integration];
        const isMeteringChanged = prev?.capabilities?.[CapabilityType.Metering] !== next.capabilities[CapabilityType.Metering];

        this.adjustServiceControl();

        if (isProvisioningChanged || isOperationalModeChanged ) {
            this.setScenarioField();
            this.adjustProvisioningControls(next.capabilities[CapabilityType.Provisioning]);
        }

        if (isIntegrationChanged) {
            this.adjustIntegrationControls(next.capabilities[CapabilityType.Integration]);
        }

        if (isMeteringChanged) {
            this.adjustMeteringControls(next.capabilities[CapabilityType.Metering]);
        }
    }

    async onBTPScenarioChange() {
        const formGroup = this.getProvisioningFormGroup();
        this.operationalScenario = formGroup?.get(STEP_SETUP_FORM_CONTROLS.SCENARIO)?.value;
        this.setLandscapeValue();
        this.adjustProvisioningControls(true);
        this.showDeprecatedMsg();
    }

    private showDeprecatedMsg() {
        const scenario = this.getProvisioningFormGroup().get(STEP_SETUP_FORM_CONTROLS.SCENARIO)?.value;
        if (scenario === OperationalScenario.UCA_SPFI && this.isOfferedInSapBtp && this.isSapManaged()) {
            this._dialogService.openDialog(ConfirmationDialogComponent, new ConfirmationDialogConfig({
                headerGlyph: 'triangle-exclamation',
                title: $localize`:XMSG|SPFI deprecation warning title@@organizations.unified-step-setup.spfiDeprecationTitle:Deprecation of Unified Cloud Automation with SPFI`,
                bodyText: $localize`:XMSG|SPFI deprecation warning@@organizations.unified-step-setup.spfiDeprecationMessage:Using Unified Cloud Automation with SPFI has been deprecated.<br/><br/>It remains available only for applications that were previously onboarded using SPFI and need to set up additional dev or test landscapes for the same application.`,
                confirmText: 'OK',
                cancelText: undefined
            }), undefined, '33rem');
        }
    }

    async onLandscapeChange(landscape: string) {
        this.subaccounts = undefined;
        const provisioningFormGroup = this.getProvisioningFormGroup();
        provisioningFormGroup.get(STEP_SETUP_FORM_CONTROLS.GLOBAL_ACCOUNT).reset();
        provisioningFormGroup.get(STEP_SETUP_FORM_CONTROLS.SUBACCOUNT).reset();
        provisioningFormGroup.get(STEP_SETUP_FORM_CONTROLS.TEMPLATE_SUBACCOUNT).reset();
        if(landscape) {
            await this.loadGlobalAccounts(landscape);
        }
    }

    async onGlobalAccountChange(globalAccount: GlobalAccountModel) {
        if (!globalAccount) {
            return;
        }
        this.getProvisioningFormGroup().get(STEP_SETUP_FORM_CONTROLS.SUBACCOUNT).reset();
        this.getProvisioningFormGroup().get(STEP_SETUP_FORM_CONTROLS.TEMPLATE_SUBACCOUNT).reset();
        const landscape = this.getProvisioningFormGroup().get(STEP_SETUP_FORM_CONTROLS.LANDSCAPE).value;
        await this.loadSubaccounts(globalAccount.subdomain, landscape);
    }

    initControls() {
        const cldSystemRoleControl = this.generatePropertyControl(ResourceType.SERVICE_METADATA, "commercialMappingConfigurations.items.properties.cldSystemRole", "", false);
        const tenantProductTypeControl = this.generatePropertyControl(ResourceType.SERVICE_METADATA, "commercialMappingConfigurations.items.properties.tenantProductType", "", false);
        const defaultStagingParameter = (this.showStagingParameters && this.stagingParameters.length) > 0 ? this.stagingParameters[0].infix : "";
        const maxDisplayNameLength = this.showStagingParameters ? 18 : 25;
        this.form.addControl(STEP_SETUP_FORM_GROUPS.STEP_SETUP, this.fb.group({
            [STEP_SETUP_FORM_CONTROLS.APP_DISPLAY_NAME]: [
                "", [Validators.required, Validators.maxLength(maxDisplayNameLength), CustomValidators.ValidatePattern(APP_DISPLAY_NAME_PATTERN,
                    $localize`:XMSG|BTP Wizard display name validation@@organizations.dialogs.create-btp-application-dialog.btp-step-setup.appDisplayName:Invalid input. The value can contain only alphabetical letters (a-z, A-Z), numbers (0-9), and spaces.`)]
            ],
            [STEP_SETUP_FORM_CONTROLS.STAGING_PARAMETER]: [defaultStagingParameter],
            operationalData: this.fb.group({
                operationalMode: [
                    undefined, [Validators.required]
                ],
                capabilities: this.fb.group({
                    [CapabilityType.Commercialization]: [{value: true, disabled: true}],
                    [CapabilityType.Provisioning]: [false],
                    [CapabilityType.Integration]: [false],
                    [CapabilityType.Metering]: [false]
                })
            }),
            [STEP_SETUP_FORM_GROUPS.PRODUCT_METADATA]: this.fb.group({
                [STEP_SETUP_FORM_CONTROLS.CLD_SYSTEM_ROLE]: cldSystemRoleControl,
                [STEP_SETUP_FORM_CONTROLS.TENANT_PRODUCT_TYPE]: tenantProductTypeControl
            }),
            [STEP_SETUP_FORM_GROUPS.PROVISIONING]: this.fb.group({
                [STEP_SETUP_FORM_CONTROLS.SCENARIO]: [OperationalScenario.UCA_LEASED],
                [STEP_SETUP_FORM_CONTROLS.IS_REUSING_SPC]: [true],
                [STEP_SETUP_FORM_CONTROLS.IS_BTP_APPLICATION]: ["yes"],
                [STEP_SETUP_FORM_CONTROLS.GLOBAL_ACCOUNT]: [undefined],
                [STEP_SETUP_FORM_CONTROLS.SUBACCOUNT]: [undefined],
                [STEP_SETUP_FORM_CONTROLS.TEMPLATE_SUBACCOUNT]: [undefined],
                [STEP_SETUP_FORM_CONTROLS.LANDSCAPE]: [undefined]
            })
        }));

        this.cldSystemRoleLinker.link(cldSystemRoleControl);
        this.cldSystemRoleLinker.link(this.form.get(PAYLOAD_FORM_GROUPS.PAYLOAD).get(PAYLOAD_FORM_GROUPS.SERVICE_METADATA).get(PAYLOAD_FORM_CONTROLS.SERVICE_METADATA.CLD_SYSTEM_ROLE) as FormControl);
        this.tenantProductTypeLinker.link(tenantProductTypeControl);
        this.tenantProductTypeLinker.link(
            this.form
                .get(PAYLOAD_FORM_GROUPS.PAYLOAD)
                .get(PAYLOAD_FORM_GROUPS.SERVICE_METADATA)
                .get(PAYLOAD_FORM_CONTROLS.SERVICE_METADATA.TENANT_PRODUCT_TYPE) as FormControl);
    }

    getOperationalDataFormGroup() {
        return this.form.get(STEP_SETUP_FORM_GROUPS.STEP_SETUP).get(STEP_SETUP_FORM_GROUPS.OPERATIONAL_DATA) as FormGroup;
    }

    getProvisioningFormGroup() {
        return this.form.get(STEP_SETUP_FORM_GROUPS.STEP_SETUP).get(STEP_SETUP_FORM_GROUPS.PROVISIONING) as FormGroup;
    }

    globalAccountDisplayFn(subaccount: GlobalAccountModel) {
        return subaccount?.displayName;
    }

    subaccountDisplayFn = (subaccount: SubaccountModel) => {
        const displayName = subaccount?.displayName;
        const hasMultiple = this.subaccounts?.filter(s => s.displayName === displayName).length > 1;
        return hasMultiple ? `${displayName} <span class="unified-wizard-subaccount-id-wrapper">(${subaccount.guid})</span>` : displayName;
    };

    serviceDisplayFunc(service: ServiceModel) {
        return service ? service.name : '';
    }

    servicesFilterFunc(content: ServiceModel[], searchTerm: string): ServiceModel[] {
        if (!searchTerm) {
            return content;
        }
        return content.filter((service) => (service.name.includes(searchTerm) || service.key.includes(searchTerm)));
    }

    adjustMeteringControls(isMeteringVisible: boolean) {
        if (isMeteringVisible) {
            this.addMetering();
        } else {
            this.removeMetering();
        }
    }

    adjustServiceControl() {
        const formGroup = this.form.get(STEP_SETUP_FORM_GROUPS.STEP_SETUP).get(STEP_SETUP_FORM_GROUPS.PRODUCT_METADATA) as FormGroup;
        const isProvisioningEnabled = this.getOperationalDataFormGroup().get(STEP_SETUP_FORM_GROUPS.CAPABILITIES).value[CapabilityType.Provisioning];
        this.isServiceVisible = this.isCustomerManaged() || (this.isOfferedInSapBtp || !isProvisioningEnabled);
        if (this.isServiceVisible) {
            const serviceFormControl = new FormControl('');
            formGroup.addControl(STEP_SETUP_FORM_CONTROLS.SERVICE, serviceFormControl);
        } else {
            formGroup.removeControl(STEP_SETUP_FORM_CONTROLS.SERVICE);
        }
    }

    getStagingInfix() {
        const stagingParameterControl = this.form.get(STEP_SETUP_FORM_GROUPS.STEP_SETUP)?.get(STEP_SETUP_FORM_CONTROLS.STAGING_PARAMETER);
        return stagingParameterControl?.value || "";
    }

    hasStagingInfix() {
        return this.getStagingInfix().length;
    }

    public getMaxDisplayNameLength(): number {
        return this.showStagingParameters ? 18 : 25;
    }

    onAppDisplayNameChange(value: string) {
        const payload = this.form.get(PAYLOAD_FORM_GROUPS.PAYLOAD);
        const infix = this.getStagingInfix();
        const displayName = WizardUtils.buildDisplayName(value, infix, this.stagingParameters);
        const technicalName = WizardUtils.convertToTechnicalName(value, infix);
        const type = WizardUtils.convertToType(displayName);
        const plural = WizardUtils.convertToPlural(displayName);
        const singular = WizardUtils.convertToSingular(displayName);
        const newPath = `${this.dialogRef.data.path}/${technicalName}`;
        const updatedGroup = WizardUtils.generateGroup(newPath);

        payload.patchValue({
            [PAYLOAD_FORM_CONTROLS.BASE_FOLDER_NAME]: technicalName,
            [PAYLOAD_FORM_CONTROLS.BASE_FOLDER_DISPLAY_NAME]: displayName,
            [PAYLOAD_FORM_CONTROLS.MANAGED_SERVICE_NAME]: `${technicalName}-ms`,
            [PAYLOAD_FORM_GROUPS.RESOURCE_TYPE_DEFINITION]: {
                [PAYLOAD_FORM_CONTROLS.RESOURCE_TYPE_DEFINITION.NAME]: `${plural}.${updatedGroup}`,
                [PAYLOAD_FORM_CONTROLS.RESOURCE_TYPE_DEFINITION.GROUP]: updatedGroup,
                [PAYLOAD_FORM_CONTROLS.RESOURCE_TYPE_DEFINITION.TYPE]: type,
                [PAYLOAD_FORM_CONTROLS.RESOURCE_TYPE_DEFINITION.PLURAL]: plural,
                [PAYLOAD_FORM_CONTROLS.RESOURCE_TYPE_DEFINITION.SINGULAR]: singular
            },
            [PAYLOAD_FORM_GROUPS.SERVICE_METADATA]: {
                [PAYLOAD_FORM_CONTROLS.SERVICE_METADATA.NAME]: `${technicalName}-metadata`,
                [PAYLOAD_FORM_CONTROLS.SERVICE_METADATA.APP_DISPLAY_NAME]: displayName,
                [PAYLOAD_FORM_CONTROLS.SERVICE_METADATA.APP_DESCRIPTION]: displayName,
                [PAYLOAD_FORM_CONTROLS.SERVICE_METADATA.APP_TECHNICAL_NAME]: technicalName
            },
            [PAYLOAD_FORM_GROUPS.SERVICE_PUBLISH_CONFIGURATION]: {
                [PAYLOAD_FORM_CONTROLS.SERVICE_PUBLISH_CONFIGURATION.NAME]: `${technicalName}-publish-config`
            },
            [PAYLOAD_FORM_GROUPS.AUTOMATION_SOLUTION]: {
                [PAYLOAD_FORM_CONTROLS.AUTOMATION_SOLUTION.NAME]: `${technicalName}-automation`,
                [PAYLOAD_FORM_CONTROLS.AUTOMATION_SOLUTION.DISPLAY_NAME]: `${displayName} Automation Solution`
            },
            [PAYLOAD_FORM_GROUPS.SPFI_APPLICATION]: {
                [PAYLOAD_FORM_CONTROLS.SPFI_APPLICATION.NAME]: `${technicalName}-spfi-app`
            },
            [PAYLOAD_FORM_GROUPS.MANAGED_APPLICATION_PROVIDER]: {
                [PAYLOAD_FORM_CONTROLS.MANAGED_APPLICATION_PROVIDER.NAME]: `${technicalName}-map`,
                [PAYLOAD_FORM_CONTROLS.MANAGED_APPLICATION_PROVIDER.APPLICATION_TYPE]: `SAP ${displayName}`,
                [PAYLOAD_FORM_CONTROLS.MANAGED_APPLICATION_PROVIDER.APPLICATION_NAMESPACE]: `sap.${WizardUtils.convertToNamespace(displayName)}`
            },
            [PAYLOAD_FORM_GROUPS.INTEGRATIONS]: {
                [PAYLOAD_FORM_CONTROLS.INTEGRATIONS.NAME]: `${technicalName}-integrations`
            },
            [PAYLOAD_FORM_GROUPS.CLD_TENANT_SYNC_ENABLEMENT]: {
                [PAYLOAD_FORM_CONTROLS.CLD_TENANT_SYNC_ENABLEMENT.NAME]: `${technicalName}-cld-sync`
            },
            [PAYLOAD_FORM_GROUPS.METERING_ACCOUNT]: {
                [PAYLOAD_FORM_CONTROLS.METERING_ACCOUNT.NAME]: `${technicalName}-account`,
            },
        }, {emitEvent: false}); // To avoid unnecessary change detection
    }


    addAutomationSolutionFormGroup() {
        const appDisplayName = this.form.get(STEP_SETUP_FORM_GROUPS.STEP_SETUP).get(STEP_SETUP_FORM_CONTROLS.APP_DISPLAY_NAME).value;
        const infix = this.getStagingInfix();
        const displayName = WizardUtils.buildDisplayName(appDisplayName, infix, this.stagingParameters);
        const technicalName = WizardUtils.convertToTechnicalName(appDisplayName, infix);

        const payload = this.form.get(PAYLOAD_FORM_GROUPS.PAYLOAD) as FormGroup;
        payload.addControl(PAYLOAD_FORM_GROUPS.AUTOMATION_SOLUTION, this.fb.group({
            name: [`${technicalName}-automation`, [Validators.required, Validators.maxLength(63), CustomValidators.ValidateTechnicalName]],
            displayName: this.generatePropertyControl(ResourceType.AUTOMATION_SOLUTION, "solution.properties.cloudAutomationSolution.properties.displayName", `${displayName} Automation Solution`),
            description: this.generatePropertyControl(ResourceType.AUTOMATION_SOLUTION, "solution.properties.cloudAutomationSolution.properties.description", "My automation solution's description"),
            supportInfo: this.generatePropertyControl(ResourceType.AUTOMATION_SOLUTION, "solution.properties.cloudAutomationSolution.properties.supportInfo", "For support, reach out to...")
        }));
    }

    removeAutomationSolutionFormGroup() {
        const payload = this.form.get(PAYLOAD_FORM_GROUPS.PAYLOAD) as FormGroup;
        payload.removeControl(PAYLOAD_FORM_GROUPS.AUTOMATION_SOLUTION);
    }

    addSpfiApplicationFormGroup() {
        const appDisplayName = this.form.get(STEP_SETUP_FORM_GROUPS.STEP_SETUP).get(STEP_SETUP_FORM_CONTROLS.APP_DISPLAY_NAME).value;
        const technicalName = WizardUtils.convertToTechnicalName(appDisplayName, this.getStagingInfix());
        const payload = this.form.get(PAYLOAD_FORM_GROUPS.PAYLOAD) as FormGroup;
        payload.addControl(PAYLOAD_FORM_GROUPS.SPFI_APPLICATION, this.fb.group({
            [PAYLOAD_FORM_CONTROLS.SPFI_APPLICATION.NAME]: [`${technicalName}-spfi-app`, [Validators.required, Validators.maxLength(63), CustomValidators.ValidateTechnicalName]],
            [PAYLOAD_FORM_CONTROLS.SPFI_APPLICATION.ENDPOINT]: this.generatePropertyControl(ResourceType.SPFI_APPLICATION, "endpoint", this.operationalScenario === OperationalScenario.SPC_SPFI ?  this.spcSpfiEndpoint : "https://my-spfi-endpoint.com"),
            [PAYLOAD_FORM_CONTROLS.SPFI_APPLICATION.CLIENT_ID]: this.generatePropertyControl(ResourceType.SPFI_APPLICATION, "trustDetails.properties.operation.properties.clientId", "my-spfi-clientid"),
            [PAYLOAD_FORM_CONTROLS.SPFI_APPLICATION.ISSUER_NAME]: this.generatePropertyControl(ResourceType.SPFI_APPLICATION, "trustDetails.properties.operation.properties.issuerName", "my-spfi-issuer-name")
        }));
    }

    removeSpfiApplicationFormGroup() {
        const payload = this.form.get(PAYLOAD_FORM_GROUPS.PAYLOAD) as FormGroup;
        payload.removeControl(PAYLOAD_FORM_GROUPS.SPFI_APPLICATION);
    }

    addManagedApplicationProviderFormGroup() {
        const payload = this.form.get(PAYLOAD_FORM_GROUPS.PAYLOAD) as FormGroup;
        const appDisplayName = this.form.get(STEP_SETUP_FORM_GROUPS.STEP_SETUP).get(STEP_SETUP_FORM_CONTROLS.APP_DISPLAY_NAME).value;
        const infix = this.getStagingInfix();
        const displayName = WizardUtils.buildDisplayName(appDisplayName, infix, this.stagingParameters);
        const technicalName = WizardUtils.convertToTechnicalName(appDisplayName, infix);
        const applicationTypeControl = this.generatePropertyControl(ResourceType.MANAGED_APPLICATION_PROVIDER, "productName", `SAP ${displayName}`);
        applicationTypeControl.addValidators([CustomValidators.ValidateStartsWith("SAP ", 'Invalid input. Must start with "SAP" and followed by your application type: SAP <my-application-type>')]);
        const applicationNamespace = this.generatePropertyControl(ResourceType.MANAGED_APPLICATION_PROVIDER, "namespace", `sap.${WizardUtils.convertToNamespace(displayName)}`);
        applicationNamespace.addValidators([
            CustomValidators.ValidatePattern(/^sap\.[a-z0-9]+(\.[a-z0-9]+)*$/, 'Invalid input. Must start with \'sap.\' followed by your application name in lowercase: sap.<my.application.name>')
        ]);

        payload.addControl(PAYLOAD_FORM_GROUPS.MANAGED_APPLICATION_PROVIDER, this.fb.group({
            [PAYLOAD_FORM_CONTROLS.MANAGED_APPLICATION_PROVIDER.NAME]: [`${technicalName}-map`, [Validators.required, Validators.maxLength(63), CustomValidators.ValidateTechnicalName]],
            [PAYLOAD_FORM_CONTROLS.MANAGED_APPLICATION_PROVIDER.APPLICATION_TYPE]: applicationTypeControl,
            [PAYLOAD_FORM_CONTROLS.MANAGED_APPLICATION_PROVIDER.APPLICATION_NAMESPACE]: applicationNamespace,
            [PAYLOAD_FORM_CONTROLS.MANAGED_APPLICATION_PROVIDER.SUPPORT_COMPONENT]: [""]
        }));
    }

    removeManagedApplicationProviderFormGroup() {
        const payload = this.form.get(PAYLOAD_FORM_GROUPS.PAYLOAD) as FormGroup;
        payload.removeControl(PAYLOAD_FORM_GROUPS.MANAGED_APPLICATION_PROVIDER);
    }

    addIntegrations() {
        const payload = this.form.get(PAYLOAD_FORM_GROUPS.PAYLOAD) as FormGroup;
        const appDisplayName = this.form.get(STEP_SETUP_FORM_GROUPS.STEP_SETUP).get(STEP_SETUP_FORM_CONTROLS.APP_DISPLAY_NAME).value;
        const infix = this.getStagingInfix();
        const technicalName = WizardUtils.convertToTechnicalName(appDisplayName, infix);
        payload.addControl(PAYLOAD_FORM_GROUPS.INTEGRATIONS, this.fb.group({
            [PAYLOAD_FORM_CONTROLS.INTEGRATIONS.NAME]: [`${technicalName}-integrations`, [Validators.required, Validators.maxLength(63), CustomValidators.ValidateTechnicalName]],
        }));
    }

    removeIntegrations() {
        const payload = this.form.get(PAYLOAD_FORM_GROUPS.PAYLOAD) as FormGroup;
        payload.removeControl(PAYLOAD_FORM_GROUPS.INTEGRATIONS);
    }

    addCLDTenantSyncEnablement() {
        if(this.hasStagingInfix()) {
            return;
        }
        const payload = this.form.get(PAYLOAD_FORM_GROUPS.PAYLOAD) as FormGroup;
        const appDisplayName = this.form.get(STEP_SETUP_FORM_GROUPS.STEP_SETUP).get(STEP_SETUP_FORM_CONTROLS.APP_DISPLAY_NAME).value;
        const technicalName = WizardUtils.convertToTechnicalName(appDisplayName, this.getStagingInfix());
        payload.addControl(PAYLOAD_FORM_GROUPS.CLD_TENANT_SYNC_ENABLEMENT, this.fb.group({
            [PAYLOAD_FORM_CONTROLS.CLD_TENANT_SYNC_ENABLEMENT.NAME]: [`${technicalName}-cld-sync`, [Validators.required, Validators.maxLength(63), CustomValidators.ValidateTechnicalName]],
        }));
    }

    removeCLDTenantSyncEnablement() {
        const payload = this.form.get(PAYLOAD_FORM_GROUPS.PAYLOAD) as FormGroup;
        payload.removeControl(PAYLOAD_FORM_GROUPS.CLD_TENANT_SYNC_ENABLEMENT);
    }

    addMetering() {
        const payload = this.form.get(PAYLOAD_FORM_GROUPS.PAYLOAD) as FormGroup;
        const baseAppDisplayName = this.form.get(STEP_SETUP_FORM_GROUPS.STEP_SETUP).get(STEP_SETUP_FORM_CONTROLS.APP_DISPLAY_NAME).value;
        const technicalName = WizardUtils.convertToTechnicalName(baseAppDisplayName, this.getStagingInfix());
        const defaultLocalTenantType = '';
        const localTenantTypeFormControl = this.generatePropertyControl(ResourceType.ACCOUNT, "security.properties.localTenantTypes.items", defaultLocalTenantType);
        payload.addControl(PAYLOAD_FORM_GROUPS.METERING_ACCOUNT, this.fb.group({
            [PAYLOAD_FORM_CONTROLS.METERING_ACCOUNT.NAME]: [`${technicalName}-metering`, [Validators.required, Validators.maxLength(63), CustomValidators.ValidateTechnicalName]],
            [PAYLOAD_FORM_CONTROLS.METERING_ACCOUNT.LOCAL_TENANT_TYPE]: localTenantTypeFormControl,
        }));

        const serviceMetadataFormGroup = payload.get(PAYLOAD_FORM_GROUPS.SERVICE_METADATA) as FormGroup;
        const serviceMetadataLocalTenantType = this.generatePropertyControl(ResourceType.ACCOUNT, "commercialMappingConfigurations.items.properties.localTenantType", defaultLocalTenantType);
        serviceMetadataFormGroup.addControl(PAYLOAD_FORM_CONTROLS.SERVICE_METADATA.LOCAL_TENANT_TYPE, serviceMetadataLocalTenantType);

        const localTenantTypeStepFormControl = this.generatePropertyControl(ResourceType.ACCOUNT, "security.properties.localTenantTypes.items", defaultLocalTenantType);

        const step1FormGroup = this.form.get(STEP_SETUP_FORM_GROUPS.STEP_SETUP) as FormGroup;
        step1FormGroup.addControl(STEP_SETUP_FORM_GROUPS.METERING, this.fb.group({
            [STEP_SETUP_FORM_CONTROLS.LOCAL_TENANT_TYPE]: localTenantTypeStepFormControl,
        }));
        this.localTenantTypeLinker.link(localTenantTypeFormControl);
        this.localTenantTypeLinker.link(localTenantTypeStepFormControl);
        this.localTenantTypeLinker.link(serviceMetadataLocalTenantType);
    }

    removeMetering() {
        const payload = this.form.get(PAYLOAD_FORM_GROUPS.PAYLOAD) as FormGroup;
        payload.removeControl(PAYLOAD_FORM_GROUPS.METERING_ACCOUNT);
        const serviceMetadataFormGroup = payload.get(PAYLOAD_FORM_GROUPS.SERVICE_METADATA) as FormGroup;
        serviceMetadataFormGroup.removeControl(PAYLOAD_FORM_CONTROLS.SERVICE_METADATA.LOCAL_TENANT_TYPE);

        const step1FormGroup = this.form.get(STEP_SETUP_FORM_GROUPS.STEP_SETUP) as FormGroup;
        step1FormGroup.removeControl(STEP_SETUP_FORM_GROUPS.METERING);
    }


    adjustProvisioningControls(isProvisioningEnabled: boolean) {
        this.adjustServiceControl();

        this.removeAutomationSolutionFormGroup();
        this.removeSpfiApplicationFormGroup();
        this.removeCLDTenantSyncEnablement();

        if (isProvisioningEnabled) {
            this.handleProvisioningScenarios();
        }

        // Handle required validators for BTP fields based on visibility
        const provisioningFormGroup = this.getProvisioningFormGroup();
        const globalAccountControl = provisioningFormGroup.get(STEP_SETUP_FORM_CONTROLS.GLOBAL_ACCOUNT);
        const subaccountControl = provisioningFormGroup.get(STEP_SETUP_FORM_CONTROLS.SUBACCOUNT);
        const isBTPFieldsVisible = isProvisioningEnabled && (this.isCustomerManaged() || this.isOfferedInSapBtp);

        if (isBTPFieldsVisible) {
            globalAccountControl?.setValidators([Validators.required]);
            subaccountControl?.setValidators([Validators.required]);
        } else {
            globalAccountControl?.clearValidators();
            subaccountControl?.clearValidators();
        }
        globalAccountControl?.updateValueAndValidity();
        subaccountControl?.updateValueAndValidity();

    }

    private handleProvisioningScenarios() {
        const formGroup = this.getProvisioningFormGroup();
        const isBTPApplication = formGroup?.get(STEP_SETUP_FORM_CONTROLS.IS_BTP_APPLICATION)?.value;
        const isCustomerManaged = this.isCustomerManaged();

        this.operationalScenario = formGroup?.get(STEP_SETUP_FORM_CONTROLS.SCENARIO)?.value;
        if (isCustomerManaged) {
            this.addAutomationSolutionFormGroup();
        } else {
            if (isBTPApplication === IsBTPApplication.YES) {
                if (this.operationalScenario === OperationalScenario.UCA_SPFI) {
                    this.addAutomationSolutionFormGroup();
                    this.addSpfiApplicationFormGroup();
                    this.addCLDTenantSyncEnablement();
                } else if (this.operationalScenario === OperationalScenario.UCA_LEASED) {
                    this.addAutomationSolutionFormGroup();
                    this.addCLDTenantSyncEnablement();
                } else if (this.operationalScenario === OperationalScenario.EUPORIE) {
                    this.addAutomationSolutionFormGroup();
                    this.addSpfiApplicationFormGroup();
                }
            } else {
                if (this.isReusingSPC) {
                    this.addCLDTenantSyncEnablement();
                    this.addSpfiApplicationFormGroup();
                } else {
                    if (this.operationalScenario === OperationalScenario.OWN) {
                        this.addSpfiApplicationFormGroup();
                        this.addCLDTenantSyncEnablement();
                    } else if (this.operationalScenario === OperationalScenario.UCA_LEASED) {
                        this.addAutomationSolutionFormGroup();
                        this.addCLDTenantSyncEnablement();
                    }
                }
            }
        }
    }

    adjustIntegrationControls(isIntegrationEnabled: boolean) {
        this.removeManagedApplicationProviderFormGroup();
        this.removeIntegrations();
        if (isIntegrationEnabled) {
            this.addManagedApplicationProviderFormGroup();
            this.addIntegrations();
        }
    }

    generatePropertyControl(resourceType: ResourceType, propertyPath: string, defaultValue: string, required = true) {
        const dialogData = this.dialogRef.data;
        const r = dialogData?.applicationResources.find((r: any) => r.type === resourceType);
        const spec = r?.versions?.[0]?.schema?.jsonSchema?.properties?.spec?.properties;
        const prop = this.nestedValuePipe.transform(spec, propertyPath);
        const control = Utils.getFormControl(prop, defaultValue, required);
        return control;
    }

    ngOnDestroy() {
        this.subscriptions.forEach(s => s.unsubscribe());
    }

    getServices() {
        return this.httpClient.get<ServiceModel[]>(this.servicesJsonUrl);
    }
}
